import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Clock,
  Truck,
  DollarSign,
  Globe,
  Phone,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import { COMPANY, SERVICES, TRUST_INDICATORS } from "../lib/constants";
import QuoteForm from "../components/QuoteForm";

const HERO_BG =
  "https://images.pexels.com/photos/11053643/pexels-photo-11053643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const trustIcons = [ShieldCheck, Clock, Truck, DollarSign, Globe];

const GMAIL_COMPOSE = `https://mail.google.com/mail/?view=cm&fs=1&to=${COMPANY.email}&su=${encodeURIComponent("Auto Transport Inquiry")}&body=${encodeURIComponent("Hi Ferrer team,\n\nI'd like to speak to a coordinator about shipping my vehicle.\n\nDetails:\n- Pickup location:\n- Delivery location:\n- Vehicle:\n- Preferred date:\n\nThank you,")}`;

const handleCoordinatorClick = () => {
  // Don't preventDefault — let the <a target="_blank"> navigate naturally (never blocked)
  if (navigator.clipboard) {
    navigator.clipboard.writeText(COMPANY.email).catch(() => {});
  }
  toast.success(`Email copied: ${COMPANY.email}`, {
    description: "Opening Gmail in a new tab…",
    duration: 4000,
  });
};

const Home = () => {
  return (
    <div data-testid="home-page" className="bg-[#050914] text-white">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-end overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="Cinematic auto transport carrier on highway"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050914]/80 via-[#050914]/60 to-[#050914]" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pb-20 pt-32 md:pb-32 md:pt-40 w-full">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-8 animate-fade-up">
              <span className="h-px w-10 bg-slate-400" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-slate-300">
                Woodbridge, NJ · Nationwide
              </span>
            </div>
            <h1 className="font-display font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] text-white animate-fade-up delay-100">
              Nationwide
              <br />
              Auto Transport
              <br />
              <span className="text-silver">Done Right.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed animate-fade-up delay-200">
              Safe. Fast. Reliable. Professional vehicle shipping from New Jersey to anywhere in
              the country — handled by a family-operated team that treats every car like it's
              their own.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-up delay-300">
              <Link
                to="#quote"
                data-testid="hero-cta-quote"
                className="group bg-white text-[#050914] px-8 py-5 text-[12px] uppercase tracking-[0.25em] font-semibold hover:bg-slate-200 transition-all flex items-center justify-center gap-3"
              >
                Get Free Quote
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="hero-cta-call"
                className="group border border-white/30 text-white px-8 py-5 text-[12px] uppercase tracking-[0.25em] font-semibold hover:bg-white hover:text-[#050914] transition-all flex items-center justify-center gap-3"
              >
                <Phone className="w-4 h-4" />
                Call {COMPANY.phone}
              </a>
            </div>

            <div className="mt-16 flex flex-wrap gap-x-10 gap-y-4 animate-fade-up delay-400">
              {["Licensed & Insured", "Family Operated", "All 48 States"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section data-testid="trust-section" className="border-t border-white/10 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-4">
                Why Choose Ferrer
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-white max-w-2xl">
                The standard your vehicle deserves.
              </h2>
            </div>
            <p className="text-slate-400 max-w-md leading-relaxed">
              Five non-negotiables we bring to every transport — from a single sedan to a
              full dealer fleet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {TRUST_INDICATORS.map((t, i) => {
              const Icon = trustIcons[i % trustIcons.length];
              return (
                <div
                  key={t.title}
                  data-testid={`trust-card-${i}`}
                  className="bg-[#050914] p-8 lg:p-10 hover:bg-[#0B132B] transition-colors group"
                >
                  <Icon className="w-7 h-7 text-slate-300 mb-8 group-hover:text-white transition-colors" />
                  <div className="font-display font-semibold text-lg text-white mb-3">{t.title}</div>
                  <p className="text-sm text-slate-400 leading-relaxed">{t.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Ferrer - large feature */}
      <section data-testid="why-section" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.pexels.com/photos/10481108/pexels-photo-10481108.png?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt="Luxury vehicle on highway"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050914] via-[#050914]/90 to-[#050914]/70" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-6">The Ferrer Difference</div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] mb-8">
              Personalized service. <span className="text-silver">Zero compromises.</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-8 max-w-lg">
              Big brokers hand your car off and disappear. We don't. Every shipment is coordinated by
              the same team from the first call to final delivery — with constant communication, safe
              handling, and reliable scheduling that simply works.
            </p>
            <a
              href={GMAIL_COMPOSE}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCoordinatorClick}
              data-testid="why-call-cta"
              className="inline-flex items-center gap-3 text-white border-b border-white/30 pb-2 text-sm uppercase tracking-[0.25em] hover:border-white cursor-pointer"
            >
              Speak to a coordinator <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <div className="lg:col-span-7 lg:pl-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {[
                { k: "01", t: "Personalized Service", d: "Direct relationship from quote to delivery — not a call center." },
                { k: "02", t: "Reliable Scheduling", d: "We commit to pickup windows and we hit them. Predictably." },
                { k: "03", t: "Constant Communication", d: "Updates at every milestone — pickup, in-transit, delivery." },
                { k: "04", t: "Safe Vehicle Handling", d: "Vetted carriers, strict inspection protocols, full coverage." },
              ].map((item) => (
                <div key={item.k} className="bg-[#050914]/80 p-8 backdrop-blur-sm">
                  <div className="text-slate-500 text-sm font-mono mb-6">{item.k}</div>
                  <div className="font-display font-semibold text-xl text-white mb-3">{item.t}</div>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-4">Services</div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-white max-w-2xl">
                Every kind of move.
                <br />
                <span className="text-silver">One trusted team.</span>
              </h2>
            </div>
            <Link
              to="#quote"
              data-testid="services-quote-link"
              className="text-sm uppercase tracking-[0.25em] text-slate-300 hover:text-white border-b border-white/30 pb-1 inline-flex items-center gap-2"
            >
              Get pricing <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <article
                key={s.title}
                data-testid={`service-card-${i}`}
                className="group border border-white/10 bg-[#0B132B]/40 hover:border-slate-400/50 transition-colors overflow-hidden"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#050914]">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover img-hover opacity-90 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] text-slate-300 bg-[#050914]/70 backdrop-blur-md px-3 py-1.5 border border-white/10">
                    0{i + 1}
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-display font-semibold text-xl text-white mb-3">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section
        id="quote"
        data-testid="quote-section"
        className="py-24 md:py-32 border-t border-white/10 scroll-mt-24"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-4">Free Quote</div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Lock in pricing in <span className="text-silver">under an hour.</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Tell us where, when, and what you're shipping. A coordinator returns your quote with
              real numbers — never a generic estimate.
            </p>
          </div>

          <QuoteForm />

          <div className="mt-12 text-center text-sm text-slate-400">
            Prefer a quick call?{" "}
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="quote-section-phone"
              className="text-white border-b border-white/30 hover:border-white"
            >
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
